import React, { ReactElement } from 'react';
import { Container, Link, Typography, makeStyles } from '@material-ui/core';
import { colors } from '../colors';
import { useTitle } from '../utils';

const useStyles = makeStyles(() => ({
  h2: {
    fontWeight: 600,
    marginBottom: '0.4em',
    '@media only screen and (max-width: 425px) ': {
      fontSize: '1.5em',
    },
  },
  h3: {
    fontWeight: 500,
    marginBottom: '0.4em',
    '@media only screen and (max-width: 425px) ': {
      fontSize: '1.25em',
    },
  },
  h4: {
    fontWeight: 500,
    marginBottom: '0.4em',
    '@media only screen and (max-width: 425px) ': {
      fontSize: '0.938em',
    },
  },
  body: {
    color: colors.black,
    textAlign: 'left',
    fontStyle: 'normal',
    fontWeight: 'normal',
    '@media only screen and (max-width: 425px) ': {
      fontSize: '0.75em',
    },
    lineHeight: '20px',
    marginBottom: '1em',
  },
  link: {
    color: colors.red1,
  },
}));

/**
 * Policies Component – Displays the privacy policy for CUAPTS.
 *
 * @remarks
 * This component displays the privacy policy, describing data collection, usage, user rights etc.
 * It adapts content styling based on the screen size.
 *
 * @return {ReactElement} – The rendered JSX structure of the Policies page.
 */

const Policies = (): ReactElement => {
  const { h2, h3, h4, body, link } = useStyles();

  useTitle('Policies');

  return (
    <Container maxWidth="lg">
      <Typography variant="h2" className={h2}>
        Privacy Policy
      </Typography>
      <Typography variant="body2" className={body}>
        Your privacy is important to us. It is CU Apartments&#39; policy to respect your privacy and
        comply with any applicable law and regulation regarding any personal information we may
        collect about you, including across our website,{' '}
        <a className={link} href="https://cuapts.org" target="_blank" rel="noreferrer">
          https://cuapts.org
        </a>
        , and other sites we own and operate.
      </Typography>
      <Typography variant="body2" className={body}>
        This policy is effective as of 2 November 2021 and was last updated on 3 September 2023.{' '}
      </Typography>
      <Typography variant="h3" className={h3}>
        Information We Collect
      </Typography>
      <Typography variant="body2" className={body}>
        Information we collect includes both information you knowingly and actively provide us when
        using or participating in any of our services and promotions, and any information
        automatically sent by your devices in the course of accessing our products and services.{' '}
      </Typography>
      <Typography variant="h4" className={h4}>
        Log Data
      </Typography>
      <Typography variant="body2" className={body}>
        When you visit our website, our servers may automatically log the standard data provided by
        your web browser. It may include your device’s Internet Protocol (IP) address, your browser
        type and version, the pages you visit, the time and date of your visit, the time spent on
        each page, other details about your visit, and technical details that occur in conjunction
        with any errors you may encounter.
      </Typography>
      <Typography variant="body2" className={body}>
        Please be aware that while this information may not be personally identifying by itself, it
        may be possible to combine it with other data to personally identify individual persons.{' '}
      </Typography>
      <Typography variant="h4" className={h4}>
        Collection and Use of Information
      </Typography>
      <Typography variant="body2" className={body}>
        We may collect personal information from you when you do any of the following on our
        website:
        <ul>
          <li>Use a mobile device or web browser to access our content</li>
          <li>Contact us via email, social media, or on any similar technologies</li>
          <li>When you mention us on social media</li>
        </ul>
      </Typography>
      <Typography variant="body2" className={body}>
        We may collect, hold, use, and disclose information for the following purposes, and personal
        information will not be further processed in a manner that is incompatible with these
        purposes.
      </Typography>
      <Typography variant="body2" className={body}>
        We use Hotjar in order to better understand our users’ needs and to optimize this service
        and experience. Hotjar is a technology service that helps us better understand our users’
        experience (e.g. how much time they spend on which pages, which links they choose to click,
        what users do and don’t like, etc.) and this enables us to build and maintain our service
        with user feedback. Hotjar uses cookies and other technologies to collect data on our users’
        behavior and their devices. This includes a device's IP address (processed during your
        session and stored in a de-identified form), device screen size, device type (unique device
        identifiers), browser information, geographic location (country only), and the preferred
        language used to display our website. Hotjar stores this information on our behalf in a
        pseudonymized user profile. Hotjar is contractually forbidden to sell any of the data
        collected on our behalf.
        <br />
        For further details, please see{' '}
        <Link target="_blank" href="https://www.hotjar.com/legal/policies/privacy/">
          Hotjar’s privacy policy
        </Link>
        .
      </Typography>
      <Typography variant="body2" className={body}>
        Please be aware that we may combine information we collect about you with general
        information or research data we receive from other trusted sources.
      </Typography>
      <Typography variant="h4" className={h4}>
        Information from Integrated Services like Google
      </Typography>
      <Typography variant="body2" className={body}>
        You can log in to our site using integrated services like Google. This allows for Google to
        authenticate your identity as a Cornell student and to provide you the option to share
        certain personal information such as your name and email. If you choose to provide such
        information, you are giving CU Apartments the permission to use, share, and store it in a
        manner consistent with this Privacy Policy.
      </Typography>
      <Typography variant="h4" className={h4}>
        Security of Your Personal Information
      </Typography>
      <Typography variant="body2" className={body}>
        When we collect and process personal information, and while we retain this information, we
        will protect it within commercially acceptable means to prevent loss and theft, as well as
        unauthorized access, disclosure, copying, use, or modification.
      </Typography>
      <Typography variant="body2" className={body}>
        Although we will do our best to protect the personal information you provide to us, we
        advise that no method of electronic transmission or storage is 100% secure, and no one can
        guarantee absolute data security. We will comply with laws applicable to us in respect of
        any data breach.
      </Typography>
      <Typography variant="body2" className={body}>
        You are responsible for selecting any password and its overall security strength, ensuring
        the security of your own information within the bounds of our services.{' '}
      </Typography>
      <Typography variant="h4" className={h4}>
        How Long We Keep Your Personal Information
      </Typography>
      <Typography variant="body2" className={body}>
        We keep your personal information only for as long as we need to. This time period may
        depend on what we are using your information for, in accordance with this privacy policy. If
        your personal information is no longer required, we will delete it or make it anonymous by
        removing all details that identify you.
      </Typography>
      <Typography variant="body2" className={body}>
        However, if necessary, we may retain your personal information for our compliance with a
        legal, accounting, or reporting obligation or for archiving purposes in the public interest,
        scientific, or historical research purposes or statistical purposes.
      </Typography>
      <Typography variant="h3" className={h3}>
        Children’s Privacy
      </Typography>
      <Typography variant="body2" className={body}>
        We do not aim any of our products or services directly at children under the age of 13, and
        we do not knowingly collect personal information about children under 13.{' '}
      </Typography>
      <Typography variant="h3" className={h3}>
        Disclosure of Personal Information to Third Parties
      </Typography>
      <Typography variant="body2" className={body}>
        We may disclose personal information to:{' '}
        <ul>
          <li>a parent, subsidiary, or affiliate of our company</li>
          <li>
            third party service providers for the purpose of enabling them to provide their
            services, for example, IT service providers, data storage, hosting and server providers,
            advertisers, or analytics platforms
          </li>
          <li>our employees, contractors, and/or related entities</li>
          <li>our existing or potential agents or business partners</li>
          <li>sponsors or promoters of any competition, sweepstakes, or promotion we run</li>
          <li>
            courts, tribunals, regulatory authorities, and law enforcement officers, as required by
            law, in connection with any actual or prospective legal proceedings, or in order to
            establish, exercise, or defend our legal rights
          </li>
          <li>
            third parties, including agents or sub-contractors, who assist us in providing
            information, products, services, or direct marketing to you third parties to collect and
            process data
          </li>
        </ul>
      </Typography>
      <Typography variant="h3" className={h3}>
        International Transfers of Personal Information
      </Typography>
      <Typography variant="body2" className={body}>
        The personal information we collect is stored and/or processed where we or our partners,
        affiliates, and third-party providers maintain facilities. Please be aware that the
        locations to which we store, process, or transfer your personal information may not have the
        same data protection laws as the country in which you initially provided the information. If
        we transfer your personal information to third parties in other countries: (i) we will
        perform those transfers in accordance with the requirements of applicable law; and (ii) we
        will protect the transferred personal information in accordance with this privacy policy.{' '}
      </Typography>
      <Typography variant="h3" className={h3}>
        Your Rights and Controlling Your Personal Information
      </Typography>
      <Typography variant="body2" className={body}>
        You always retain the right to withhold personal information from us, with the understanding
        that your experience of our website may be affected. We will not discriminate against you
        for exercising any of your rights over your personal information. If you do provide us with
        personal information you understand that we will collect, hold, use and disclose it in
        accordance with this privacy policy. You retain the right to request details of any personal
        information we hold about you.
      </Typography>
      <Typography variant="body2" className={body}>
        If we receive personal information about you from a third party, we will protect it as set
        out in this privacy policy. If you are a third party providing personal information about
        somebody else, you represent and warrant that you have such person’s consent to provide the
        personal information to us.
      </Typography>
      <Typography variant="body2" className={body}>
        If you have previously agreed to us using your personal information for direct marketing
        purposes, you may change your mind at any time. We will provide you with the ability to
        unsubscribe from our email-database or opt out of communications. Please be aware we may
        need to request specific information from you to help us confirm your identity.
      </Typography>
      <Typography variant="body2" className={body}>
        If you believe that any information we hold about you is inaccurate, out of date,
        incomplete, irrelevant, or misleading, please contact us using the details provided in this
        privacy policy. We will take reasonable steps to correct any information found to be
        inaccurate, incomplete, misleading, or out of date.
      </Typography>
      <Typography variant="body2" className={body}>
        If you believe that we have breached a relevant data protection law and wish to make a
        complaint, please contact us using the details below and provide us with full details of the
        alleged breach. We will promptly investigate your complaint and respond to you, in writing,
        setting out the outcome of our investigation and the steps we will take to deal with your
        complaint. You also have the right to contact a regulatory body or data protection authority
        in relation to your complaint.
      </Typography>
      <Typography variant="h3" className={h3}>
        Use of Cookies
      </Typography>
      <Typography variant="body2" className={body}>
        We use &ldquo;cookies&rdquo; to collect information about you and your activity across our
        site. A cookie is a small piece of data that our website stores on your computer, and
        accesses each time you visit, so we can understand how you use our site. This helps us serve
        you content based on preferences you have specified.
      </Typography>
      <Typography variant="h3" className={h3}>
        Limits of Our Policy
      </Typography>
      <Typography variant="body2" className={body}>
        Our website may link to external sites that are not operated by us. Please be aware that we
        have no control over the content and policies of those sites, and cannot accept
        responsibility or liability for their respective privacy practices.
      </Typography>
      <Typography variant="h3" className={h3}>
        Changes to This Policy
      </Typography>
      <Typography variant="body2" className={body}>
        At our discretion, we may change our privacy policy to reflect updates to our business
        processes, current acceptable practices, or legislative or regulatory changes. If we decide
        to change this privacy policy, we will post the changes here at the same link by which you
        are accessing this privacy policy.
      </Typography>
      <Typography variant="body2" className={body}>
        If required by law, we will get your permission or give you the opportunity to opt in to or
        opt out of, as applicable, any new uses of your personal information.
      </Typography>
      <Typography variant="h3" className={h3}>
        Contact Us
      </Typography>
      <Typography variant="body2" className={body}>
        For any questions or concerns regarding your privacy, you may contact us here:
      </Typography>
      <Typography variant="body2" className={body}>
        <Link href="mailto:hello@cornelldti.org">hello@cornelldti.org</Link>
      </Typography>
    </Container>
  );
};

export default Policies;
